import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  CircularProgress,
  Autocomplete,
  Stack,
  useTheme,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from "notistack";
import { CustomerAPI, CustomerUpdateRequest } from "../../QueueBoard/api/customerApi";
import { useWorkOrderStore } from "../../store/zustandWorkOrderStore";
import type { NewCustomer } from "../types/customer";
import type { CityStateZip } from "../../../models/lookupTypes";
import type { Theme } from "@mui/material/styles";
import { LookupAPI } from "../../QueueBoard/api/lookupDataApi";
import { dropdownMenuProps } from "components/QueueBoard/filter-panel/FilterPanelStyles";

const newCustomerAccordionCardSx = (theme: Theme) => ({
  borderRadius: "10px",
  border: "1px solid",
  borderColor:
    theme.palette.mode === "light" ? "#e8e5e0" : "rgba(255, 255, 255, 0.08)",
  backgroundColor: theme.palette.mode === "light" ? "#ebeced" : "#2c2b28",
  boxShadow:
    theme.palette.mode === "light"
      ? "0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)"
      : "0 2px 12px rgba(0, 0, 0, 0.32)",
  "&:before": { display: "none" },
  overflow: "hidden",
});

const accordionSectionTitleSx = {
  fontWeight: 700,
  textTransform: "capitalize" as const,
  color: "text.primary",
  fontSize: "0.8125rem",
  fontStyle: "normal",
};

const accordionSummarySx = {
  px: 2.5,
  py: 1.25,
  minHeight: 52,
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    my: 0.5,
  },
};

const accordionDetailsSx = {
  px: 2.5,
  pb: 2.5,
  pt: 0,
  "& .MuiOutlinedInput-root": {
    bgcolor: "background.paper",
  },
};

export interface NewCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  locationIdentity: number | null | undefined;
  entBy: string;
  onCustomerCreated?: (customerId: number) => void;
  /** Optional: Pass customerId to enable edit mode */
  customerId?: number;
  /** Mode: 'create' (default) or 'edit' */
  mode?: "create" | "edit";
}

/** Client-side max lengths (aligned with API / business rules). */
const FIELD_MAX = {
  CustomerNumber: 15,
  CustomerName: 50,
  BillingAddress1: 50,
  BillingAddress2: 50,
  PhoneNumber: 25,
  City: 50,
  Country: 50,
  State: 2,
  Zip: 10,
  Email: 100,
} as const;

type LengthValidatedField = keyof typeof FIELD_MAX;

const maxLenMsg = (max: number) =>
  `Maximum ${max} character${max === 1 ? "" : "s"} allowed.`;

const truncate = (value: string, max: number) =>
  value.length <= max ? value : value.slice(0, max);

/** Split full customer display name into first / middle / last for the create payload. */
function parseCustomerNameParts(fullName: string): {
  firstName: string;
  middleName: string;
  lastName: string;
} {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return { firstName: "", middleName: "", lastName: "" };
  }
  if (words.length === 1) {
    return { firstName: words[0], middleName: "", lastName: "" };
  }
  if (words.length === 2) {
    return { firstName: words[0], middleName: "", lastName: words[1] };
  }
  return {
    firstName: words[0],
    middleName: words.slice(1, -1).join(" "),
    lastName: words[words.length - 1],
  };
}

/** City lookup row may expose country as ISO, name, id, or mixed JSON casing. */
function readCityRowCountry(row: CityStateZip): string | undefined {
  const r = row as unknown as Record<string, unknown>;
  const raw = row.country ?? r.Country;
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim();
  }
  if (typeof raw === "number") {
    return String(raw);
  }
  return undefined;
}

/** Alpha-3 → alpha-2 when DB stores ISO-3166-1 alpha-3 in Country column. */
const ISO3166_ALPHA3_TO_ALPHA2: Record<string, string> = {
  USA: "US",
  GBR: "GB",
  CAN: "CA",
  MEX: "MX",
  AUS: "AU",
};

interface CustomerFormState {
  CustomerNumber: string;
  BillingAddress1: string;
  BillingAddress2: string;
  City: string;
  County: string;
  State: string;
  Zip: string;
  Country: string;
  BillingZoneId: number | null;
  PhoneNumber: string;
  Email: string;
  CustomerName: string;
  FirstName: string;
  LastName: string;
  MiddleName: string;
}

const emptyForm = (): CustomerFormState => ({
  CustomerNumber: "",
  BillingAddress1: "",
  BillingAddress2: "",
  City: "",
  County: "",
  State: "",
  Zip: "",
  Country: "",
  BillingZoneId: null,
  PhoneNumber: "",
  Email: "",
  CustomerName: "",
  FirstName: "",
  LastName: "",
  MiddleName: "",
});

const NewCustomerDialog = ({
  open,
  onClose,
  locationIdentity,
  entBy,
  onCustomerCreated,
  customerId,
  mode = "create",
}: NewCustomerDialogProps) => {
  const storeCustomerZones = useWorkOrderStore((s) => s.customerZones);
  const storeCountries = useWorkOrderStore((s) => s.countries);
  const storeStates = useWorkOrderStore((s) => s.states);
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(emptyForm);
  const [cityOptions, setCityOptions] = useState<CityStateZip[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  /** MUI Autocomplete does not reopen the list when `options` updates after an async fetch; control `open` and reopen when results arrive. */
  const [cityAutocompleteOpen, setCityAutocompleteOpen] = useState(false);
  const [selectedCityRow, setSelectedCityRow] = useState<CityStateZip | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<LengthValidatedField, string>>
  >({});
  const cityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const isEditMode = mode === "edit" && customerId !== undefined;

  const defaultCountryIso = useMemo(() => {
    const us = storeCountries.find((c) => c.isoCode?.toUpperCase() === "US");
    return us?.isoCode ?? "";
  }, [storeCountries]);

  const lookupsLoading =
    storeStates.length === 0 ||
    storeCountries.length === 0 ||
    storeCustomerZones.length === 0;

  const resetForm = useCallback(() => {
    setForm(emptyForm());
    setCityOptions([]);
    setSelectedCityRow(null);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    resetForm();
    setForm((prev) => ({
      ...prev,
      Country: prev.Country || defaultCountryIso,
    }));
  }, [open, resetForm, defaultCountryIso]);

  // Fetch customer details when in edit mode
  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (isEditMode && customerId && open) {
        setLoadingCustomer(true);
        try {
          const details = await CustomerAPI.getCustomerDetails(customerId);
          if (details) {
            setForm({
              CustomerNumber: details.customerNumber || "",
              CustomerName: details.customerName || "",
              FirstName: details.firstName || "",
              LastName: details.lastName || "",
              MiddleName: details.middleName || "",
              BillingAddress1: details.billingAddress1 || "",
              BillingAddress2: details.billingAddress2 || "",
              City: details.city || "",
              County: details.county || "",
              State: details.state || "",
              Zip: details.zip || "",
              Country: details.country || defaultCountryIso,
              BillingZoneId: details.billingZoneId ?? null,
              PhoneNumber: details.phoneNumber || "",
              Email: details.email || "",
            });
          } else {
            enqueueSnackbar("Failed to load customer details", { variant: "error" });
          }
        } catch (error) {
          enqueueSnackbar("Failed to load customer details", { variant: "error" });
        } finally {
          setLoadingCustomer(false);
        }
      }
    };

    fetchCustomerDetails();
  }, [isEditMode, customerId, open, defaultCountryIso, enqueueSnackbar]);

  const applyCountryFromLookup = useCallback(
    (rowCountry: string | undefined) => {
      const trimmed = rowCountry?.trim();
      if (!trimmed) {
        return defaultCountryIso;
      }
      const lower = trimmed.toLowerCase();
      const upper = trimmed.toUpperCase();

      // 1) Match store country by ISO code (city row often stores same value as Country.isoCode)
      const byIso = storeCountries.find(
        (c) => c.isoCode && c.isoCode.toUpperCase() === upper,
      );
      if (byIso?.isoCode) {
        return byIso.isoCode;
      }

      // 2) Match by display name
      const byName = storeCountries.find(
        (c) => c.countryName?.toLowerCase() === lower,
      );
      if (byName?.isoCode) {
        return byName.isoCode;
      }

      // 3) Country column may store SettingsCountry id
      if (/^\d+$/.test(trimmed)) {
        const id = Number(trimmed);
        const byId = storeCountries.find((c) => c.countryId === id);
        if (byId?.isoCode) {
          return byId.isoCode;
        }
      }

      // 4) Alpha-3 in DB → resolve to alpha-2 then match store
      const alpha2 = ISO3166_ALPHA3_TO_ALPHA2[upper];
      if (alpha2) {
        const byAlpha2 = storeCountries.find(
          (c) => c.isoCode?.toUpperCase() === alpha2,
        );
        if (byAlpha2?.isoCode) {
          return byAlpha2.isoCode;
        }
      }

      // Keep dropdown consistent with MenuItem values (isoCode); avoid raw strings that do not match any option
      return defaultCountryIso;
    },
    [storeCountries, defaultCountryIso],
  );

  const applyCityRow = useCallback(
    (row: CityStateZip) => {
      const country = applyCountryFromLookup(readCityRowCountry(row));
      setForm((prev) => ({
        ...prev,
        City: truncate(row.city ?? "", FIELD_MAX.City),
        County: row.county ?? "",
        Zip: truncate(row.zip ?? "", FIELD_MAX.Zip),
        State: truncate(row.state ?? "", FIELD_MAX.State),
        Country: truncate(country, FIELD_MAX.Country),
      }));
      setSelectedCityRow(row);
    },
    [applyCountryFromLookup],
  );

  const scheduleCityLookup = useCallback(
    (fragment: string) => {
      if (cityTimerRef.current) {
        clearTimeout(cityTimerRef.current);
      }
      cityTimerRef.current = setTimeout(() => {
        void (async () => {
          if (!locationIdentity || fragment.trim().length < 2) {
            setCityOptions([]);
            return;
          }
          setCityLoading(true);
          try {
            const rows = await LookupAPI.getCityStateZip(
              locationIdentity,
              fragment,
            );
            setCityOptions(rows);
            if (rows.length > 0) {
              queueMicrotask(() => setCityAutocompleteOpen(true));
            }
          } finally {
            setCityLoading(false);
          }
        })();
      }, 400);
    },
    [locationIdentity],
  );

  useEffect(() => {
    return () => {
      if (cityTimerRef.current) {
        clearTimeout(cityTimerRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateLengthConstraints = useCallback((): boolean => {
    const countryValue = form.Country || defaultCountryIso;
    const next: Partial<Record<LengthValidatedField, string>> = {};
    const check = (k: LengthValidatedField, value: string) => {
      if (value.length > FIELD_MAX[k]) {
        next[k] = maxLenMsg(FIELD_MAX[k]);
      }
    };
    check("CustomerNumber", form.CustomerNumber);
    check("CustomerName", form.CustomerName);
    check("BillingAddress1", form.BillingAddress1);
    check("BillingAddress2", form.BillingAddress2);
    check("PhoneNumber", form.PhoneNumber);
    check("City", form.City);
    check("Country", countryValue);
    if (form.State.length > FIELD_MAX.State) {
      next.State = maxLenMsg(FIELD_MAX.State);
    } else if (form.State.length > 0 && form.State.length !== FIELD_MAX.State) {
      next.State = "Use a 2-letter state abbreviation.";
    }
    check("Zip", form.Zip);
    check("Email", form.Email);
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }, [form, defaultCountryIso]);

  const bindLimitedField = (key: LengthValidatedField) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setForm((prev) => ({ ...prev, [key]: truncate(v, FIELD_MAX[key]) }));
      setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    },
    error: Boolean(fieldErrors[key]),
    helperText: fieldErrors[key] ?? "",
    inputProps: { maxLength: FIELD_MAX[key] },
  });

  const handleSubmit = async () => {
    if (!locationIdentity) {
      enqueueSnackbar("Select a location before saving customer.", {
        variant: "warning",
      });
      return;
    }
    const countryValue = form.Country || defaultCountryIso;
    
    // Required fields - Customer number only required for create mode
    const requiredFields: Array<{ label: string; value: string }> = [
      { label: "Customer name", value: form.CustomerName },
      { label: "Email", value: form.Email },
      { label: "Phone number", value: form.PhoneNumber },
      { label: "Billing address 1", value: form.BillingAddress1 },
      { label: "City", value: form.City },
      { label: "State", value: form.State },
      { label: "Zip", value: form.Zip },
      { label: "Country", value: countryValue },
    ];
    
    // Add customer number validation only for create mode
    if (!isEditMode) {
      requiredFields.unshift({ label: "Customer number", value: form.CustomerNumber });
    }
    
    // Check BillingZoneId separately since it's a number
    const missingZone = form.BillingZoneId === null || form.BillingZoneId === undefined;

    const missingFields = requiredFields
      .filter((fieldItem) => !fieldItem.value.trim())
      .map((fieldItem) => fieldItem.label);
    
    if (missingZone) {
      missingFields.push("Billing Zone");
    }

    if (missingFields.length > 0) {
      enqueueSnackbar(
        `Please complete all required fields: ${missingFields.join(", ")}.`,
        { variant: "warning" },
      );
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode && customerId) {
        // Update existing customer
        const updatePayload: CustomerUpdateRequest = {
          customerId: customerId,
          locationId: locationIdentity,
          customerName: form.CustomerName.trim(),
          firstName: form.FirstName,
          lastName: form.LastName,
          middleName: form.MiddleName,
          billingAddress1: form.BillingAddress1,
          billingAddress2: form.BillingAddress2,
          city: form.City,
          county: form.County,
          state: form.State,
          zip: form.Zip,
          country: countryValue,
          billingZoneId: form.BillingZoneId ?? undefined,
          phoneNumber: form.PhoneNumber,
          email: form.Email,
          modifiedBy: entBy,
        };

        await CustomerAPI.updateCustomer(updatePayload);
        enqueueSnackbar("Customer updated successfully.", { variant: "success" });
        onCustomerCreated?.(customerId);
        handleClose();
      } else {
        // Create new customer
        const exists = await CustomerAPI.isCustomerExist(form.CustomerNumber);
        if (exists) {
          enqueueSnackbar(
            "This customer number already exists. Please choose a different number.",
            { variant: "error" },
          );
          return;
        }

        const trimmedCustomerName = form.CustomerName.trim();
        const nameParts = parseCustomerNameParts(trimmedCustomerName);

        const payload: NewCustomer = {
          CustomerId: 0,
          AddressId: 0,
          LocationId: locationIdentity,
          CustomerNumber: form.CustomerNumber.trim(),
          BillingAddress1: form.BillingAddress1,
          BillingAddress2: form.BillingAddress2,
          City: form.City,
          County: form.County,
          State: form.State,
          Zip: form.Zip,
          Country: countryValue,
          BillingZoneId: form.BillingZoneId,
          PhoneNumber: form.PhoneNumber,
          Email: form.Email,
          CustomerName: trimmedCustomerName,
          FirstName: nameParts.firstName,
          MiddleName: nameParts.middleName,
          LastName: nameParts.lastName,
          ModDate: new Date(),
          EntBy: entBy,
          EntDate: new Date(),
          ModBy: entBy,
        };

        const newCustomerId = await CustomerAPI.createNewCustomer(payload);
        if (newCustomerId == null) {
          enqueueSnackbar("Could not create customer. Please try again.", {
            variant: "error",
          });
          return;
        }
        enqueueSnackbar("Customer created successfully.", { variant: "success" });
        onCustomerCreated?.(newCustomerId);
        handleClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const theme = useTheme();
  return (
    <Dialog
      PaperProps={{
        sx: {
          borderRadius: 4,
        },
      }}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.primary.main
              : "#181F2A",
          color:
            theme.palette.mode === "light"
              ? theme.palette.primary.contrastText
              : "#fff",
          boxSizing: "border-box",
          height: 59,
          minHeight: 59,
          display: "flex",
          alignItems: "center",
          py: 0,
          px: 2,
        })}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Typography
            pl="15px"
            sx={{
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "16px",
            }}
          >
            {isEditMode ? "Edit Customer" : "Add New Customer"}
          </Typography>

          <IconButton sx={{ color: "white" }} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loadingCustomer ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={(theme) => newCustomerAccordionCardSx(theme)}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{ color: "text.secondary", fontSize: 22 }}
                />
              }
              sx={accordionSummarySx}
            >
              <Typography variant="subtitle2" sx={accordionSectionTitleSx}>
                Personal info
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsSx}>
              <Grid container spacing={1.5}>
                {/* Customer Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...bindLimitedField("CustomerName")}
                    label={
                      <span>
                        Customer name <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading}
                  />
                </Grid>
                {/* Customer Number */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...bindLimitedField("CustomerNumber")}
                    label={
                      <span>
                        Customer # {!isEditMode && <span style={{ color: "red" }}>*</span>}
                      </span>
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading || isEditMode}
                    InputProps={{
                      readOnly: isEditMode,
                    }}
                  />
                </Grid>

                {/* <Grid item xs={12} sm={4}>
                  <TextField
                    {...field("FirstName")}
                    label="First name"
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    {...field("LastName")}
                    label="Last name"
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    {...field("MiddleName")}
                    label="Middle name"
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading}
                  />
                </Grid> */}
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={(theme) => newCustomerAccordionCardSx(theme)}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{ color: "text.secondary", fontSize: 22 }}
                />
              }
              sx={accordionSummarySx}
            >
              <Typography variant="subtitle2" sx={accordionSectionTitleSx}>
                Contact info
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsSx}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...bindLimitedField("Email")}
                    label={
                      <span>
                        Email <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...bindLimitedField("PhoneNumber")}
                    label={
                      <span>
                        Phone number <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={(theme) => newCustomerAccordionCardSx(theme)}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{ color: "text.secondary", fontSize: 22 }}
                />
              }
              sx={accordionSummarySx}
            >
              <Typography variant="subtitle2" sx={accordionSectionTitleSx}>
                Address
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsSx}>
              <Grid container spacing={1.5}>
                {/* Billing address 1 */}
                <Grid item xs={12}>
                  <TextField
                    {...bindLimitedField("BillingAddress1")}
                    label={
                      <span>
                        Billing address 1{" "}
                        <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading}
                  />
                </Grid>
                {/* Billing address 2 */}
                <Grid item xs={12}>
                  <TextField
                    {...bindLimitedField("BillingAddress2")}
                    label="Billing address 2"
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading}
                  />
                </Grid>
                {/* Zip */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    {...bindLimitedField("Zip")}
                    label={
                      <span>
                        Zip Code <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={lookupsLoading}
                  />
                </Grid>
                {/* City */}
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    freeSolo
                    open={cityAutocompleteOpen}
                    onOpen={() => setCityAutocompleteOpen(true)}
                    onClose={() => setCityAutocompleteOpen(false)}
                    options={cityOptions}
                    loading={cityLoading}
                    value={selectedCityRow}
                    inputValue={form.City}
                    getOptionLabel={(opt) =>
                      typeof opt === "string"
                        ? opt
                        : `${opt.city ?? ""}, ${opt.state ?? ""} ${opt.zip ?? ""}`
                    }
                    isOptionEqualToValue={(a, b) =>
                      typeof a !== "string" &&
                      typeof b !== "string" &&
                      a.cityStateLookupId === b.cityStateLookupId
                    }
                    filterOptions={(opts) => opts}
                    onInputChange={(_, v, reason) => {
                      if (reason === "input" || reason === "clear") {
                        const v2 = truncate(v, FIELD_MAX.City);
                        setForm((prev) => ({ ...prev, City: v2 }));
                        setSelectedCityRow(null);
                        setFieldErrors((prev) => ({
                          ...prev,
                          City: undefined,
                        }));
                        scheduleCityLookup(v2);
                      }
                    }}
                    onChange={(_, v) => {
                      if (
                        v &&
                        typeof v === "object" &&
                        "cityStateLookupId" in v
                      ) {
                        applyCityRow(v as CityStateZip);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <span>
                            City <span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        size="small"
                        fullWidth
                        disabled={!locationIdentity || lookupsLoading}
                        helperText={
                          !locationIdentity
                            ? "Select a work order location to look up cities."
                            : undefined
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {cityLoading ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                {/* State */}
                <Grid item xs={12} sm={4}>
                  <FormControl
                    fullWidth
                    size="small"
                    disabled={lookupsLoading}
                    error={Boolean(fieldErrors.State)}
                  >
                    <InputLabel id="new-cust-state-label">
                      State <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <Select
                      labelId="new-cust-state-label"
                      label="State *"
                      value={form.State}
                      MenuProps={dropdownMenuProps(theme)}
                      onChange={(e) => {
                        const next = truncate(
                          e.target.value as string,
                          FIELD_MAX.State,
                        );
                        setForm((prev) => ({ ...prev, State: next }));
                        setFieldErrors((prev) => ({
                          ...prev,
                          State: undefined,
                        }));
                      }}
                    >
                      {storeStates.map((s) => (
                        <MenuItem key={s.stateId} value={s.abbreviation ?? ""}>
                          {s.abbreviation} — {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.State ? (
                      <FormHelperText>{fieldErrors.State}</FormHelperText>
                    ) : null}
                  </FormControl>
                </Grid>

                {/* Country */}
                <Grid item xs={12} sm={4}>
                  <FormControl
                    fullWidth
                    size="small"
                    disabled={lookupsLoading}
                    error={Boolean(fieldErrors.Country)}
                  >
                    <InputLabel id="new-cust-country-label">
                      Country <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <Select
                      MenuProps={dropdownMenuProps(theme)}
                      labelId="new-cust-country-label"
                      label="Country *"
                      value={form.Country || defaultCountryIso}
                      onChange={(e) => {
                        const next = truncate(
                          e.target.value as string,
                          FIELD_MAX.Country,
                        );
                        setForm((prev) => ({ ...prev, Country: next }));
                        setFieldErrors((prev) => ({
                          ...prev,
                          Country: undefined,
                        }));
                      }}
                    >
                      {storeCountries.map((c) => (
                        <MenuItem key={c.countryId} value={c.isoCode ?? ""}>
                          {c.countryName}
                          {c.isoCode ? ` (${c.isoCode})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.Country ? (
                      <FormHelperText>{fieldErrors.Country}</FormHelperText>
                    ) : null}
                  </FormControl>
                </Grid>
                {/* Billing Zone */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small" disabled={lookupsLoading}>
                    <InputLabel id="new-cust-zone-label">
                      Billing Zone <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <Select
                      MenuProps={dropdownMenuProps(theme)}
                      labelId="new-cust-zone-label"
                      label="Billing Zone *"
                      value={form.BillingZoneId ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm((prev) => ({
                          ...prev,
                          BillingZoneId: value === "" ? null : Number(value),
                        }));
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {storeCustomerZones.map((z) => (
                        <MenuItem key={z.zoneId} value={z.zoneId}>
                          {z.displayText}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          sx={{ textTransform: "capitalize" }}
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          sx={{ textTransform: "capitalize" }}
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting || lookupsLoading || loadingCustomer || !locationIdentity}
        >
          {submitting ? <CircularProgress size={22} /> : isEditMode ? "Save" : "Create Customer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewCustomerDialog;
